import { useEffect, useMemo, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { useAppState } from "../state/AppState";
import { useLang } from "../i18n/LangContext";
import { formatPrice } from "../state/fxRates";
import {
  headlineCardPrice,
  lowestDayPrice,
  priceUnitLabel,
} from "../state/pricing";
import { isInSeasonOn, spotSourceKey, todayIso } from "../state/filters";
import type { Species, Spot, SpotResourceLink } from "../types";

// Maps species id → the chip-color class defined in web/style.css around
// line 1194. Keep these names in sync.
const SPECIES_CHIP: Record<Species, string> = {
  salmon: "chip--salmon",
  brown_trout: "chip--trout",
  arctic_char: "chip--char",
  sea_trout: "chip--seatrout",
  rainbow_trout: "chip--rainbow",
};
import {
  CalendarIcon,
  CashIcon,
  ClockIcon,
  ExternalIcon,
  FishIcon,
  HookIcon,
  InfoIcon,
  PinIcon,
  RefreshIcon,
  RodIcon,
} from "../icons";

const MONTH_ABBR = {
  de: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  is: ["jan", "feb", "mar", "apr", "maí", "jún", "júl", "ágú", "sep", "okt", "nóv", "des"],
};

function formatMonthDay(s: string | undefined, lang: "de" | "en" | "is"): string {
  if (!s) return "";
  const m = s.match(/^--(\d{2})-(\d{2})$/);
  if (!m) return s;
  const months = MONTH_ABBR[lang] ?? MONTH_ABBR.de;
  return `${parseInt(m[2], 10)}. ${months[parseInt(m[1], 10) - 1]}`;
}

function translateFishingHours(s: string | undefined, lang: "de" | "en" | "is"): string {
  if (!s) return "";
  if (lang === "is") return s;
  let out = s;
  if (lang === "de") {
    out = out
      .replace(/Allan sólarhringinn/gi, "Rund um die Uhr")
      .replace(/sólarhring(?:in)?n?/gi, "rund um die Uhr")
      .replace(/Veiðitíminn/gi, "Angelzeit")
      .replace(/kl\.\s*/gi, "");
  } else {
    out = out
      .replace(/Allan sólarhringinn/gi, "Around the clock")
      .replace(/sólarhring(?:in)?n?/gi, "around the clock")
      .replace(/Veiðitíminn/gi, "Fishing hours")
      .replace(/kl\.\s*/gi, "");
  }
  return out;
}

interface BilingualOpts {
  is?: string;
  en?: string;
  de?: string;
}

function pickBilingual(lang: "de" | "en" | "is", opts: BilingualOpts): { text: string; fallback: string | null } {
  const order = lang === "de" ? ["de", "en", "is"] : lang === "en" ? ["en", "de", "is"] : ["is", "en", "de"];
  const codes = order.map((c) => c.toUpperCase());
  for (let i = 0; i < order.length; i++) {
    const v = (opts as Record<string, string | undefined>)[order[i]];
    if (v) {
      return { text: v, fallback: i === 0 ? null : codes[i] };
    }
  }
  return { text: "", fallback: null };
}

function BodyText({ raw }: { raw: string }) {
  const paragraphs = raw.split(/\n{2,}/);
  return (
    <>
      {paragraphs.map((p, i) => {
        const lines = p.split("\n");
        if (lines.length >= 2) {
          const first = lines[0].trim();
          if (first.length > 0 && first.length <= 100 && !/[.!?]\s*$/.test(first)) {
            const body = lines.slice(1).join(" ").trim();
            return (
              <div className="section__sub" key={i}>
                <h4 className="section__sub-h">{first}</h4>
                <p>{body}</p>
              </div>
            );
          }
        }
        return <p key={i}>{p.replace(/\n/g, " ")}</p>;
      })}
    </>
  );
}

function localizeResourceLabel(r: SpotResourceLink, lang: "de" | "en" | "is"): string {
  const map = {
    de: { map: "Angelplatz-Karte", directions: "Wegbeschreibung", report: "Jahresbericht", counter: "Fischzähler" },
    en: { map: "Fishing-place map", directions: "Directions", report: "Annual report", counter: "Fish counter" },
    is: { map: "Veiðistaðakort", directions: "Leiðarlýsing", report: "Árnefndarskýrsla", counter: "Teljarinn" },
  } as const;
  const localized = map[lang] ?? map.de;
  if (r.kind && r.kind in localized) return localized[r.kind as keyof typeof localized];
  return (lang === "de" && r.labelDe) || (lang === "en" && r.labelEn) || r.label;
}

interface SpotDetailProps {
  spot: Spot;
  onClose: () => void;
  /** When true, applies `.detail--hidden` so CSS can slide it off-screen
   *  with the same 250ms easing as the basemap-switch shift. */
  hidden?: boolean;
}

export function SpotDetail({ spot, onClose, hidden }: SpotDetailProps) {
  const { t, lang, speciesLabel, methodLabel } = useLang();
  const { currency, rates, filters } = useAppState();

  const srcKey = spot.isFree ? "free" : spotSourceKey(spot);
  const cssSrc = srcKey === "free" ? "free" : srcKey.replace(".is", "");
  const headerLabel = spot.isFree ? t("freeBadge") : spot.source.name;

  const lp = !spot.isFree ? lowestDayPrice(spot) : null;
  const card = !spot.isFree && !lp
    ? headlineCardPrice(spot, t("fpMembershipShort"), t("cardYearShort"))
    : null;

  const priceUnits = useMemo(
    () => ({
      perRodDay: t("perRodDay"),
      perRodHalfDay: t("perRodHalfDay"),
      perDay: t("perDay"),
      perSeason: t("perSeason"),
      perCardYear: t("perCardYear"),
      perYear: t("perYear"),
    }),
    [t],
  );

  const seasonStatus = spot.seasonStart && spot.seasonEnd
    ? isInSeasonOn(spot, filters.date || todayIso())
    : null;

  const description = pickBilingual(lang, {
    is: spot.description,
    en: spot.descriptionEn,
    de: spot.descriptionDe,
  });

  // web/style.css ships the legacy vanilla-JS pattern where photo wrappers
  // start at opacity:0 with a shimmer overlay, and only the parent's
  // `.is-loaded` class flips them to opacity:1. The React component owns
  // that state per image — keyed by URL so it survives spot-to-spot
  // transitions when the same photo would otherwise re-flicker.
  type ImgState = "loading" | "loaded" | "error";
  const [imgStates, setImgStates] = useState<Record<string, ImgState>>({});
  // Retry counter per URL — bumped on retry-button click and appended as
  // a ?r=N query so the browser bypasses its negative cache and re-fetches.
  const [retryCounts, setRetryCounts] = useState<Record<string, number>>({});
  // Which photo index the hero currently shows (clicking a thumb swaps).
  const [activeIndex, setActiveIndex] = useState(0);
  // Reset state when the spot changes so a brand-new spot's photos go
  // through the loading flow again (and previous spot's URLs don't leak).
  useEffect(() => {
    setImgStates({});
    setRetryCounts({});
    setActiveIndex(0);
  }, [spot.id]);
  const photoStateClass = (url: string): string => {
    const s = imgStates[url];
    return s === "loaded" ? " is-loaded" : s === "error" ? " is-error" : " is-loading";
  };
  const markImg = (url: string, s: ImgState) =>
    setImgStates((prev) => (prev[url] === s ? prev : { ...prev, [url]: s }));
  const srcWithRetry = (url: string): string => {
    const n = retryCounts[url] ?? 0;
    return n === 0 ? url : `${url}${url.includes("?") ? "&" : "?"}r=${n}`;
  };
  const retryImg = (url: string) => {
    setRetryCounts((prev) => ({ ...prev, [url]: (prev[url] ?? 0) + 1 }));
    setImgStates((prev) => ({ ...prev, [url]: "loading" }));
  };

  return (
    <aside
      className={"detail" + (hidden ? " detail--hidden" : "")}
      // `inert` removes the subtree from the tab order AND blurs any
      // focused descendant — unlike aria-hidden, which would raise an
      // a11y warning when its child still has focus.
      ref={(el) => {
        if (!el) return;
        if (hidden) el.setAttribute("inert", "");
        else el.removeAttribute("inert");
      }}
    >
      <div className="detail__header">
        <Typography
          variant="h6"
          component="div"
          className="detail__header-title"
          sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}
          noWrap
        >
          {spot.name}
        </Typography>
        <Chip
          label={headerLabel}
          size="small"
          variant="outlined"
          className={`chip--src-${cssSrc}`}
          sx={{ flexShrink: 0 }}
        />
        <IconButton
          className="detail__close"
          aria-label={t("close")}
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div id="detail-body">
        {spot.images && spot.images.length > 0 && (() => {
          const images = spot.images;
          const hero = images[Math.min(activeIndex, images.length - 1)];
          return (
          <div className="section section--photos">
            <a
              className={"photos__hero" + photoStateClass(hero.url)}
              href={spot.source.url}
              target="_blank"
              rel="noopener"
            >
              <img
                key={hero.url}
                src={srcWithRetry(hero.url)}
                alt={hero.alt ?? spot.name}
                loading="eager"
                decoding="async"
                // Cache-hot fix: if the browser had the image cached, it
                // finishes loading before React attaches `onLoad`. Check
                // `complete` + `naturalHeight` on mount to catch that case.
                ref={(node) => {
                  if (node && node.complete && node.naturalHeight > 0) {
                    markImg(hero.url, "loaded");
                  }
                }}
                onLoad={() => markImg(hero.url, "loaded")}
                onError={() => markImg(hero.url, "error")}
              />
              {imgStates[hero.url] === "error" && (
                <button
                  type="button"
                  className="photos__error"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    retryImg(hero.url);
                  }}
                  aria-label={t("photoRetry")}
                >
                  <span className="photos__error-icon" aria-hidden="true">
                    <RefreshIcon />
                  </span>
                  <span className="photos__error-text">{t("photoRetry")}</span>
                </button>
              )}
            </a>
            {images.length > 1 && (
              <div className="photos__thumbs">
                {images.slice(0, 6).map((im, i) => (
                  <button
                    key={im.url}
                    type="button"
                    className={
                      "photos__thumb" +
                      (i === activeIndex ? " photos__thumb--active" : "") +
                      photoStateClass(im.url)
                    }
                    onClick={() => setActiveIndex(i)}
                    aria-label={`Foto ${i + 1}`}
                  >
                    <img
                      key={im.url}
                      src={srcWithRetry(im.url)}
                      alt=""
                      loading="eager"
                      decoding="async"
                      ref={(node) => {
                        if (node && node.complete && node.naturalHeight > 0) {
                          markImg(im.url, "loaded");
                        }
                      }}
                      onLoad={() => markImg(im.url, "loaded")}
                      onError={() => markImg(im.url, "error")}
                    />
                    {imgStates[im.url] === "error" && (
                      <span
                        className="photos__error photos__error--thumb"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          retryImg(im.url);
                        }}
                        role="button"
                        tabIndex={-1}
                        aria-label={t("photoRetry")}
                      >
                        <span className="photos__error-icon" aria-hidden="true">
                          <RefreshIcon />
                        </span>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <div className="photos__credit">
              {t("photosFrom")} {spot.source.name}
            </div>
          </div>
          );
        })()}

        <div className="section">
          <SectionHeading icon={<InfoIcon />}>{t("secKeyFacts")}</SectionHeading>
          <div className="detail__hero">
            <StatCard icon={<CashIcon />} label={t("statPrice")}>
              {spot.isFree ? (
                <Typography variant="h5" sx={{ fontWeight: 800, fontSize: 16, color: "success.light" }}>
                  {t("freeBadge")}
                </Typography>
              ) : lp ? (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.1 }}>
                    {formatPrice(lp.amount, currency, rates)}
                  </Typography>
                  {currency !== "ISK" && (
                    <Typography
                      variant="body2"
                      component="div"
                      color="text.secondary"
                      sx={{ mt: 0, mb: 0, fontSize: 16, lineHeight: 1.2 }}
                    >
                      {formatPrice(lp.amount, "ISK", rates)}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {priceUnitLabel(lp.unit, priceUnits)}
                  </Typography>
                </>
              ) : card ? (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.1 }}>
                    {formatPrice(card.amount, currency, rates)}
                  </Typography>
                  {currency !== "ISK" && (
                    <Typography
                      variant="body2"
                      component="div"
                      color="text.secondary"
                      sx={{ mt: 0, mb: 0, fontSize: 16, lineHeight: 1.2 }}
                    >
                      {formatPrice(card.amount, "ISK", rates)}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 800, fontSize: 16 }}>—</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("priceUnknown")}
                  </Typography>
                </>
              )}
            </StatCard>

            {spot.seasonStart && spot.seasonEnd && (
              <StatCard icon={<CalendarIcon />} label={t("factSeason")}>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: 16 }}>
                  {formatMonthDay(spot.seasonStart, lang)} – {formatMonthDay(spot.seasonEnd, lang)}
                </Typography>
                {seasonStatus === true && (
                  <Chip
                    size="small"
                    label={t("factInSeason")}
                    variant="outlined"
                    sx={{ mt: 0.75, color: "success.light", borderColor: "success.light" }}
                  />
                )}
                {seasonStatus === false && (
                  <Chip
                    size="small"
                    label={t("factOutOfSeason")}
                    variant="outlined"
                    sx={{ mt: 0.75, color: "secondary.main", borderColor: "secondary.main" }}
                  />
                )}
              </StatCard>
            )}

            {spot.fishingHours && (
              <StatCard icon={<ClockIcon />} label={t("factHours")}>
                <Typography variant="h5" sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.1 }}>
                  {translateFishingHours(spot.fishingHours, lang)}
                </Typography>
              </StatCard>
            )}

            {!!spot.rodCount && (
              <StatCard icon={<RodIcon />} label={t("factRodCount")} centerValue>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, lineHeight: 1, fontSize: 24 }}
                >
                  {spot.rodCount}
                </Typography>
              </StatCard>
            )}
          </div>
        </div>

        {spot.species.length > 0 && (
          <div className="section">
            <SectionHeading icon={<FishIcon />}>{t("secFishSpecies")}</SectionHeading>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {spot.species.map((s) => (
                <Chip
                  key={s}
                  icon={<FishIcon />}
                  label={speciesLabel(s)}
                  variant="outlined"
                  size="small"
                  className={SPECIES_CHIP[s] ?? ""}
                />
              ))}
            </Box>
          </div>
        )}

        {spot.methods.length > 0 && (
          <div className="section">
            <SectionHeading icon={<HookIcon />}>{t("secAllowedBait")}</SectionHeading>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {spot.methods.map((m) => (
                <Chip
                  key={m}
                  icon={<HookIcon />}
                  label={methodLabel(m)}
                  variant="outlined"
                  size="small"
                  className="chip--method"
                />
              ))}
            </Box>
          </div>
        )}

        {description.text && (
          <div className="section section--description section--bilingual">
            <SectionHeading icon={<InfoIcon />}>{t("secDescription")}</SectionHeading>
            <div>
              {description.fallback && (
                <Chip
                  size="small"
                  label={description.fallback}
                  variant="outlined"
                  sx={{ mb: 1, color: "warning.light", borderColor: "warning.light" }}
                  title={`${lang.toUpperCase()}-Übersetzung fehlt — zeige ${description.fallback}`}
                />
              )}
              <BodyText raw={description.text} />
            </div>
          </div>
        )}

        {spot.resourceLinks && spot.resourceLinks.length > 0 && (
          <div className="section">
            <SectionHeading icon={<PinIcon />}>{t("secResources")}</SectionHeading>
            <div className="detail__links">
              {spot.resourceLinks.map((r, i) => (
                <a
                  key={i}
                  className="source-link"
                  href={r.url}
                  target="_blank"
                  rel="noopener"
                  title={r.label}
                >
                  <ExternalIcon />
                  {localizeResourceLabel(r, lang)}
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="detail__links">
          <a className="source-link" href={spot.source.url} target="_blank" rel="noopener">
            <ExternalIcon />
            {t("sourceLink")}: {spot.source.name}
          </a>
          {spot.lat && spot.lon && (
            <a
              className="source-link"
              href={`https://www.google.com/maps/search/?api=1&query=${spot.lat}%2C${spot.lon}`}
              target="_blank"
              rel="noopener"
              title="Google Maps"
            >
              <PinIcon />
              {t("openInMaps")}
            </a>
          )}
        </div>
      </div>

    </aside>
  );
}

/** A section heading: small caps + accent-coloured icon, on a single line. */
function SectionHeading({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Typography
      variant="overline"
      component="h3"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        m: 0,
        mb: 0.5,
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: "0.08em",
        color: "text.secondary",
        // Centre-align the leading icon and pin its size so each
        // section heading sits at the same vertical rhythm.
        "& > svg": {
          width: 14,
          height: 14,
          flexShrink: 0,
          color: "primary.main",
          display: "block",
        },
      }}
    >
      {icon}
      {children}
    </Typography>
  );
}

/** Card for a single "key fact" (price, season, hours, rods…).
 *  `centerValue` flips the body to grid-center so a single number (e.g.
 *  rod count) sits in the middle of the card instead of stuck top-left.
 *  The Card always fills its grid cell so centering has vertical space
 *  to work with when the row mate is taller. */
function StatCard({
  icon,
  label,
  children,
  centerValue,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  centerValue?: boolean;
}) {
  return (
    <Card
      variant="outlined"
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{
          p: 1.5,
          "&:last-child": { pb: 1.5 },
          flex: 1,
          // Only switch to flex column when we need to centre a value.
          // In the default block layout, inline children like Chips keep
          // their natural width instead of stretching to the card.
          ...(centerValue
            ? { display: "flex", flexDirection: "column" }
            : {}),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 0.5,
            color: "text.secondary",
            fontSize: 10,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            "& svg": { width: 12, height: 12, color: "primary.main" },
          }}
        >
          {icon}
          {label}
        </Box>
        {centerValue ? (
          <Box
            sx={{
              flex: 1,
              display: "grid",
              placeItems: "center",
              minHeight: 48,
            }}
          >
            {children}
          </Box>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}
